<template>
    <form-container>
        <page-header v-if="props.segmentId" class="vx-mb-6 lg:vx-mb-12">
            <template v-slot:subtitle>
                <a href="/contacts/segments" class="!vx-no-underline !vx-text-sky-600 vx-text-base vx-font-normal">
                    <font-awesome-icon class="vx-mr-1" :icon="faArrowLeft"></font-awesome-icon>
                    All Segments
                </a>
            </template>

            {{ segment?.name }}
        </page-header>

        <div :class="{ 'vx-grid vx-grid-cols-12': props.segmentId }">
            <page-sidebar
                v-if="props.segmentId"
                :links="[
                    { href: `/contacts/segments/${props.segmentId}`, label: 'Details' },
                    { href: `/contacts/segments/${props.segmentId}#filters`, label: 'Filters' },
                    { href: `/contacts/segments/${props.segmentId}/contacts`, label: 'Contacts' },
                ]"
            ></page-sidebar>
            <vx-sticky-nav
                :sections="
                    [
                        { id: 'name', label: 'Name' },
                        { id: 'category', label: 'Category' },
                        { id: 'type', label: 'Type' },
                        { id: 'audience', label: 'Audience' },
                        FEATURES.contact_groups ? { id: 'group', label: 'Group' } : null,
                        { id: 'filters', label: 'Filters' },
                    ].filter((s) => !!s)
                "
                class="vx-col-span-12"
                :class="{ 'lg:vx-col-span-10': !!props.segmentId }"
                :navClasses="`${props.segmentId ? 'vx-hidden' : ''}`"
            >
                <div>
                    <page-header> Contacts </page-header>

                    <div v-if="loading && !list.length" class="vx-text-center vx-py-10">
                        <vx-icon-loading class="vx-w-9 vx-h-9 vx-animate-loading-spin"></vx-icon-loading>
                    </div>

                    <template v-else-if="!loading && list.length === 0">
                        <vx-box
                            color="info"
                            class="vx-mt-4 vx-flex vx-flex-col vx-px-8 vx-py-20 vx-gap-6 vx-items-center vx-text-xl vx-text-slate-500"
                        >
                            There are no contacts for this segment.
                        </vx-box>
                    </template>

                    <template v-else>
                        <page-controls class="vx-mb-12">
                            <template v-slot:left>
                                <div class="vx-flex vx-w-full vx-gap-2 @xl/controls:vx-w-auto">
                                    <vx-button-group size="lg" class="vx-w-full">
                                        <vx-select
                                            :modelValue="fetchParams.sort_field"
                                            :options="[
                                                {
                                                    value: 'id',
                                                    label: 'Sort by: Created',
                                                },
                                            ]"
                                        ></vx-select>
                                        <vx-button
                                            @click.prevent="
                                                fetchParams.sort_dir === 'asc'
                                                    ? (fetchParams.sort_dir = 'desc')
                                                    : (fetchParams.sort_dir = 'asc');
                                                applyFilters();
                                            "
                                            type="button"
                                            color="white"
                                            shadow
                                        >
                                            <font-awesome-icon
                                                :icon="
                                                    fetchParams.sort_dir === 'asc'
                                                        ? faArrowUpWideShort
                                                        : faArrowDownWideShort
                                                "
                                            ></font-awesome-icon>
                                        </vx-button>
                                    </vx-button-group>
                                </div>
                            </template>
                            <template v-slot:right>
                                <vx-button size="lg" block @click="tagContactsModal = true" data-test="tag-modal-bnt"
                                    >Tag Contacts <font-awesome-icon :icon="faTags"></font-awesome-icon
                                ></vx-button>
                            </template>
                        </page-controls>

                        <vx-table
                            v-if="list.length"
                            :class="{
                                'xl:vx-grid-cols-[repeat(7,auto)]': !FEATURES.contact_groups,
                                'xl:vx-grid-cols-[repeat(8,auto)]': FEATURES.contact_groups,
                            }"
                            rowBreakpoint="xl"
                        >
                            <vx-table-row isHeader>
                                <div class="xl:vx-col-span-2">Name</div>
                                <div>Phone</div>
                                <div>Marketing</div>
                                <div>Transactional</div>
                                <div v-if="FEATURES.contact_groups">Preferred</div>
                                <div>Added</div>
                            </vx-table-row>

                            <contact-row
                                v-for="(contact, index) in list"
                                :modelValue="list[index]"
                                :key="list[index].id"
                            >
                                <template v-slot:buttons>
                                    <vx-button
                                        :href="`/contacts/${list[index].id}/view`"
                                        data-test="edit"
                                        color="muted"
                                        outline
                                    >
                                        View <font-awesome-icon :icon="faEye" />
                                    </vx-button>
                                </template>
                            </contact-row>
                        </vx-table>

                        <div v-if="list.length" class="vx-mt-8 vx-flex vx-justify-between vx-w-full vx-gap-10">
                            <vx-button
                                v-if="fetchParams.cursor"
                                @click="loadData()"
                                data-test="load-more-btn"
                                color="muted-dark"
                                size="xl"
                                class="vx-grow-0"
                                :loading="loading"
                            >
                                Load More
                                <font-awesome-icon :icon="faSpinner"></font-awesome-icon>
                            </vx-button>
                        </div>
                    </template>
                </div>
            </vx-sticky-nav>
        </div>

        <modal-tag-contacts :segment="segment" v-model:visible="tagContactsModal"></modal-tag-contacts>
    </form-container>
</template>

<script setup>
import {
    VxStickyNav,
    VxButton,
    VxTable,
    VxTableRow,
    VxIconLoading,
    VxBox,
    VxSelect,
    VxButtonGroup,
} from '@voxie/frontend-components';
import PageHeader from '~/components/general/PageHeader.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    faArrowDownWideShort,
    faArrowLeft,
    faArrowUpWideShort,
    faEye,
    faSpinner,
    faTags,
} from '@fortawesome/pro-solid-svg-icons';
import { ref, inject, onMounted } from 'vue';
import segmentsClient from '~/services/segmentsClient';
import { useToasted } from '~/composables/useToasted';
import FormContainer from '~/components/general/FormContainer.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import ContactRow from '../../contact/ContactRow.vue';
import PageControls from '../../../general/PageControls.vue';
import ModalTagContacts from '../ModalTagContacts.vue';

const FEATURES = inject('FEATURES');

const toasted = useToasted();

const props = defineProps({
    segmentId: {
        type: Number,
        required: true,
    },
});

const teamId = Spark.state.currentTeam.id;
const { list, loading, fetchParams, fetchRecords } = useCursorPagination();
const segment = ref({});
const tagContactsModal = ref(false);

const defaultFilters = () => {
    return {
        cursor: null,
        per_page: 10,
        sort_field: 'id',
        sort_dir: 'asc',
    };
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };

    loadData();
};

const fetchSegment = async () => {
    try {
        const response = await segmentsClient.getSegment(teamId, props.segmentId);
        segment.value = response.data;
    } catch (error) {
        console.error(error);
        toasted.global.platform_error();
    }
};

onMounted(() => {
    fetchParams.value = defaultFilters();
    fetchSegment();
    loadData();
});

const loadData = () => {
    fetchRecords(segmentsClient.getSegmentContacts(teamId, props.segmentId, fetchParams.value));
};
</script>
